import React, { useEffect } from "react";
import { graphql, useStaticQuery, Link } from "gatsby";
import { getImage } from "gatsby-plugin-image";
import { motion } from "framer-motion";
import { Gradient } from "whatamesh";

import Seo from "../components/Seo";
import Layout from "../components/Layout";
import Cta from "../components/Cta";

const BlogPage = () => {
  return (
    <Layout>
      <Seo title="Insights & Tips on Web Design" />
      <BlogSection />
      <Cta />
    </Layout>
  );
};

const BlogSection = () => {
  const data = useStaticQuery(graphql`
    query BlogPostsQuery {
      allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/(blog)/" } }
        sort: { frontmatter: { date: DESC } }
      ) {
        edges {
          node {
            id
            fields {
              slug
            }
            frontmatter {
              title
              date(formatString: "MM/DD/YYYY")
              image {
                childImageSharp {
                  gatsbyImageData
                }
              }
            }
          }
        }
      }
    }
  `);

  useEffect(() => {
    const gradient = new Gradient();
    gradient.initGradient("#gradient-canvas-cta");
  }, []);

  return (
    <div className="overflow-hidden relative">
      <canvas
        id="gradient-canvas-cta"
        data-transition-in
        className="absolute inset-0 opacity-30 z-0"
      />
      <div className="max-w-5xl mx-auto mt-20 md:mt-32 py-36 px-4 md:py-20">
        <div className="relative mx-auto max-w-5xl">
          <p className="mb-4 text-center subtitle md:text-left">
            Some insight{" "}
          </p>
          <motion.h1
            initial={{ y: 10, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="relative max-w-lg mx-auto md:mx-0 text-center md:text-left"
          >
            Blog
          </motion.h1>
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            whileInView={{ width: 100, opacity: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="my-5 mx-auto h-1 w-20 rounded bg-brand md:mx-0"
          ></motion.div>

          <div className="">
            <div className="mx-auto">
              <div className="mx-auto mt-16 grid max-w-2xl auto-rows-fr grid-cols-1 gap-8 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
                {data.allMarkdownRemark.edges.map((post) => {
                  const image =
                    post.node.frontmatter.image?.childImageSharp
                      ?.gatsbyImageData;
                  const imageUrl = image
                    ? getImage(image).images.fallback.src
                    : "";
                  const date = post.node.frontmatter.date || "";

                  return (
                    <article
                      key={post.node.id}
                      className="relative isolate flex flex-col justify-end overflow-hidden rounded-2xl bg-gray-900 px-8 pb-8 pt-80 sm:pt-48 lg:pt-80"
                    >
                      {imageUrl && (
                        <img
                          alt={post.node.frontmatter.title || "Blog Post Image"}
                          src={imageUrl}
                          className="absolute inset-0 -z-10 size-full object-cover"
                        />
                      )}
                      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-gray-900 via-gray-900/40" />
                      <div className="absolute inset-0 -z-10 rounded-2xl ring-1 ring-inset ring-gray-900/10" />

                      <div className="flex flex-col items-start gap-y-1 overflow-hidden text-sm/6 text-gray-300">
                        <time
                          dateTime={date}
                          className="mr-8 text-brand-light font-montreal-bold"
                        >
                          {date}
                        </time>
                      </div>
                      <h3 className="mt-3 text-lg/6 font-semibold text-white">
                        <Link to={post.node.fields.slug}>
                          <span className="absolute inset-0" />
                          {post.node.frontmatter.title}
                        </Link>
                      </h3>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
